import React, { useMemo } from 'react';
import { getISTMidnightTimestamp } from '../hooks/useFirebaseData';
import TemperatureChart from './TemperatureChart';
import InventoryTracker from './InventoryTracker';
import TransactionsTable from './TransactionsTable';

const ONLINE_THRESHOLD_MS = 3 * 60 * 1000;
const DAY_SECS = 24 * 60 * 60;

function aggregateSince(orders, refundedIds, cutoffSecs) {
  let revenue = 0;
  let cups = 0;
  orders.forEach((order) => {
    if ((order.timestamp || 0) < cutoffSecs) return;
    if (refundedIds.has(order.id)) return;
    if (order.amount) revenue += Number(order.amount);
    if (order.item_id) cups += 1;
    if (!order.amount && order.status === 'PAID') {
      revenue += 19;
      cups += 1;
    }
  });
  return { revenue, cups };
}

function formatJuiceName(itemId) {
  return itemId.replace(/^juice_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function KioskDetailView({
  kioskId, orders, inventory, telemetry, tempHistory,
  refundedIds, refundQueue, onRestock, onBack,
}) {
  const kioskOrders = useMemo(
    () => orders.filter((o) => o.kiosk_id === kioskId),
    [orders, kioskId]
  );

  const kioskInventory = useMemo(
    () => (inventory[kioskId] ? { [kioskId]: inventory[kioskId] } : {}),
    [inventory, kioskId]
  );

  const kioskTempHistory = useMemo(
    () => (tempHistory[kioskId] ? { [kioskId]: tempHistory[kioskId] } : {}),
    [tempHistory, kioskId]
  );

  const data = (telemetry || {})[kioskId] || {};
  const lastSeen = data.last_seen ? new Date(data.last_seen) : null;
  const msSince = lastSeen ? Date.now() - lastSeen.getTime() : Infinity;
  const isOnline = msSince < ONLINE_THRESHOLD_MS;

  // ── Sales summary: Today (IST calendar day) + rolling 7/30-day windows ──────
  const salesPeriods = useMemo(() => {
    const nowSecs = Date.now() / 1000;
    const defs = [
      { key: 'today', label: 'Today', cutoff: getISTMidnightTimestamp() },
      { key: '7d',    label: 'Last 7 Days',  cutoff: nowSecs - 7  * DAY_SECS },
      { key: '30d',   label: 'Last 30 Days', cutoff: nowSecs - 30 * DAY_SECS },
    ];
    return defs.map((d) => ({ ...d, ...aggregateSince(kioskOrders, refundedIds, d.cutoff) }));
  }, [kioskOrders, refundedIds]);

  // ── Per-juice cup breakdown: rolling 24h/7d/30d windows ──────────────────────
  const juicePeriodDefs = useMemo(() => {
    const nowSecs = Date.now() / 1000;
    return [
      { key: '24h', label: '24h', cutoff: nowSecs - DAY_SECS },
      { key: '7d',  label: '7d',  cutoff: nowSecs - 7  * DAY_SECS },
      { key: '30d', label: '30d', cutoff: nowSecs - 30 * DAY_SECS },
    ];
  }, []);

  const juiceBreakdown = useMemo(() => {
    const itemIds = Array.from(new Set(kioskOrders.map((o) => o.item_id).filter(Boolean))).sort();
    return itemIds.map((itemId) => {
      const counts = {};
      juicePeriodDefs.forEach((p) => {
        counts[p.key] = kioskOrders.filter(
          (o) => o.item_id === itemId && (o.timestamp || 0) >= p.cutoff && !refundedIds.has(o.id)
        ).length;
      });
      return { itemId, name: formatJuiceName(itemId), counts };
    });
  }, [kioskOrders, refundedIds, juicePeriodDefs]);

  return (
    <div>
      <button
        id="kiosk-detail-back"
        onClick={onBack}
        style={{
          background: 'transparent', border: '1px solid var(--border)', borderRadius: '0.5rem',
          color: 'var(--text-primary)', padding: '0.4rem 0.9rem', fontSize: '0.85rem',
          cursor: 'pointer', marginBottom: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
        }}
      >
        ← Back to Fleet
      </button>

      <div className="section" style={{ marginBottom: '1.25rem' }}>
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-title-icon">{isOnline ? '🟢' : '🔴'}</span>
            {kioskId}
            <span style={{
              marginLeft: '0.75rem', fontSize: '0.75rem', fontWeight: 600,
              color: isOnline ? 'var(--accent-green)' : '#ff4444',
              backgroundColor: isOnline ? 'var(--accent-green-glow)' : 'rgba(255,68,68,0.15)',
              padding: '2px 10px', borderRadius: '999px',
            }}>
              {isOnline ? 'ONLINE' : 'OFFLINE'}
            </span>
          </h2>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'State', value: data.state || '—', color: 'var(--text-primary)' },
            {
              label: 'Temperature',
              value: data.temperature > -990 ? `${data.temperature.toFixed(1)}°C` : '—',
              color: data.temperature > 30 ? '#ffb347' : 'var(--accent-blue)',
            },
          ].map((s) => (
            <div key={s.label} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: '10px', padding: '0.6rem 1.2rem', minWidth: '140px',
            }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.label}</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Sales Summary: Today / 7 Days / 30 Days ─────────────────────────── */}
      <div className="section" style={{ marginBottom: '1.25rem' }}>
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-title-icon">💰</span>
            Sales Summary
          </h2>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {salesPeriods.map((p) => (
            <div key={p.key} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: '10px', padding: '0.75rem 1.25rem', minWidth: '160px', flex: '1 1 160px',
            }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>{p.label}</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--accent-green)' }}>
                ₹{p.revenue.toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--accent-blue)', marginTop: '0.15rem' }}>
                {p.cups.toLocaleString('en-IN')} cups
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Per-Juice Cup Breakdown: 24h / 7d / 30d ─────────────────────────── */}
      <div className="section" style={{ marginBottom: '1.25rem' }}>
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-title-icon">🧃</span>
            Cups by Juice
          </h2>
        </div>
        {juiceBreakdown.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No orders recorded for this kiosk yet.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Juice</th>
                  {juicePeriodDefs.map((p) => <th key={p.key}>{p.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {juiceBreakdown.map((row) => (
                  <tr key={row.itemId}>
                    <td className="table-primary">{row.name}</td>
                    {juicePeriodDefs.map((p) => (
                      <td key={p.key} className="table-accent">{row.counts[p.key]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TemperatureChart tempHistory={kioskTempHistory} />
      <InventoryTracker inventory={kioskInventory} onRestock={onRestock} />
      <TransactionsTable orders={kioskOrders} refundedIds={refundedIds} refundQueue={refundQueue} />
    </div>
  );
}
